import { useMemo } from 'react';

import { getSourceNavigationContext } from '#client/utils/secureStorage';

/**
 * Extracts the path from a URL, removing domain and query parameters.
 * Returns just the path segments without leading/trailing slashes.
 *
 * @param {string} url - The full URL to extract the path from
 * @returns {string} The path without domain and query params, or empty string if invalid
 *
 * @example
 * extractPathFromUrl('https://www.dev.zenbusiness.com/registered-agent/?_gl=123')
 * // Returns: 'registered-agent'
 *
 * extractPathFromUrl('https://example.com/dashboard/settings?foo=bar')
 * // Returns: 'dashboard/settings'
 */
export function extractPathFromUrl(url) {
  if (!url) return '';

  try {
    const parsedUrl = new URL(url);
    // Remove leading and trailing slashes from pathname
    return parsedUrl.pathname.replace(/^\/|\/$/g, '');
  } catch {
    // If URL parsing fails, return empty string
    return '';
  }
}

/**
 * Hook to generate thread context metadata for thread creation.
 *
 * Supports two modes:
 * 1. Standalone tools: Uses persistKey to generate tag, origination='standalone_tool', sourceComponent=persistKey
 * 2. Velo Chat: Uses referrer from sessionStorage/document.referrer, tag=null, sourceComponent='velo_chat'
 *
 * @param {object} options - Hook options
 * @param {string} [options.persistKey] - For standalone tools, the persist key to use for tag generation
 * @returns {object} Thread context with tag, origination, and sourceComponent
 *
 * @example
 * // Standalone tool usage
 * const threadContext = useThreadContext({ persistKey: 'business-name-generation' });
 * // Returns: { tag: null, origination: 'standalone_tool', sourceComponent: 'business-name-generation' }
 *
 * @example
 * // Velo Chat usage
 * const threadContext = useThreadContext();
 * // Returns: { tag: null, origination: 'https://example.com/dashboard', sourceComponent: 'velo_chat' }
 */
export function useThreadContext({ persistKey } = {}) {
  return useMemo(() => {
    const sourceContext = getSourceNavigationContext();
    const currentReferrer = document.referrer;
    const referrer = sourceContext?.referrer || currentReferrer;

    return {
      tag: null,
      origination: extractPathFromUrl(referrer),
      sourceComponent: persistKey || 'velo-chat'
    };
  }, [persistKey]);
}

export default useThreadContext;
